import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { Mail } from 'lucide-react';
import { useAuthStore } from '../../../stores/authStore';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { PasswordInput } from '../../../components/ui/password-input';
import { Label } from '../../../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { PublicHelpButton } from '../../../components/help/PublicHelpButton';
import { resendVerificationEmail } from '../api/authApi';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

export function LoginForm() {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuthStore();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError();
    setValidationError(null);
    setEmailNotVerified(false);
    setResendSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if Turnstile is completed (if configured)
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setValidationError('Bitte bestätigen Sie, dass Sie kein Roboter sind');
      return;
    }

    try {
      await login(formData, turnstileToken || undefined);
      navigate('/');
    } catch (error: any) {
      // Check for email not verified error
      const errorCode = error.response?.data?.error?.code;
      if (errorCode === 'EMAIL_NOT_VERIFIED') {
        setEmailNotVerified(true);
      }
      console.error('Login error:', error);
      // Reset Turnstile on error
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) return;

    setResendLoading(true);
    try {
      await resendVerificationEmail(formData.email);
      setResendSuccess(true);
    } catch (error) {
      console.error('Resend verification error:', error);
    } finally {
      setResendLoading(false);
    }
  };

  const displayError = validationError || error;

  return (
    <>
    <PublicHelpButton />
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Willkommen zurück</CardTitle>
        <CardDescription>
          Melden Sie sich bei Ihrem Konto an, um fortzufahren
        </CardDescription>
      </CardHeader>

      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {emailNotVerified && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-4 rounded-md space-y-3">
              <div className="flex items-start gap-2">
                <Mail className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">E-Mail nicht bestätigt</p>
                  <p className="mt-1">
                    Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse. Prüfen Sie Ihren Posteingang.
                  </p>
                </div>
              </div>
              {resendSuccess ? (
                <p className="text-green-700 text-center">
                  Eine neue Bestätigungsmail wurde gesendet!
                </p>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                >
                  {resendLoading ? 'Wird gesendet...' : 'Bestätigungsmail erneut senden'}
                </Button>
              )}
            </div>
          )}
          {!emailNotVerified && displayError && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
              {displayError}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="ihre@email.de"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Passwort</Label>
            <PasswordInput
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-xs text-muted-foreground hover:text-primary"
              disabled={isLoading}
            >
              Passwort vergessen?
            </button>
          </div>

          {TURNSTILE_SITE_KEY && (
            <div className="flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token: string) => {
                  setTurnstileToken(token);
                  setValidationError(null);
                }}
                onError={() => {
                  setTurnstileToken(null);
                  setValidationError('Verifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
                }}
                onExpire={() => {
                  setTurnstileToken(null);
                }}
                options={{
                  theme: 'auto',
                  language: 'de',
                }}
              />
            </div>
          )}
        </CardContent>

        <CardFooter className="flex flex-col space-y-4">
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Anmelden...' : 'Anmelden'}
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            Noch kein Konto?{' '}
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="text-primary hover:underline"
              disabled={isLoading}
            >
              Jetzt kostenlos registrieren
            </button>
          </p>
        </CardFooter>
      </form>
    </Card>
    </>
  );
}
